// React imports
import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useParams, useNavigate, useLocation, Link } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

// MUI imports
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepButton from '@mui/material/StepButton';
import { blue, green, grey, amber, red } from '@mui/material/colors';
import Checkbox from '@mui/material/Checkbox';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faPencil, faTimes, faCheck, faBullseyeArrow, faCircleCheck, faXmark, faFloppyDisk, faSquare, faSquareCheck, faRadar, faCaretLeft, faCaretRight, faTrash } from '@fortawesome/pro-light-svg-icons';

// Local imports
import * as Utils from './utils.js';
import Header from '../../Header.js';
import LegEditor from '../../LegEditor.js';
import CurrencyTextField from '../components/CurrencyTextField.js';
import OpportunityRow from '../../OpportunityRow.js';
import OpportunitiesFilterOwner from '../components/OpportunitiesFilter.owner.js';
import AutocompleteWithReactWindow from '../../AutocompleteWithReactWindow.js';
import { PIPELINE_TRADELINES as PIPELINE } from '../accounts/AccountFunnelPipeline.js';
import PIPELINE_COLORS from '../accounts/AccountFunnelPipeline.colors.js';
import MonadaTimeline from '../timeline/Timeline.js';
import ConfirmDialog from '../components/ConfirmDialog.js';
import { useTheme } from '@mui/material';

dayjs.extend(timezone);
dayjs.extend(utc);

function TradelinePage() {
    const { org, accounts, setAccounts, opportunities, onUpdateOpportunity, accesstoken, seaPorts, tradelines, onUpdateTradeline, me, exchangeRates, featureToggle } = useOutletContext();
    const { accountId, tradelineId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [ accountInputValue, setAccountInputValue ] = useState('');
    const [ createNewAccount, setCreateNewAccount ] = useState(false);
    const [ timespanJump, setTimespanJump ] = useState(0);
    const [ opportunitiesExpanded, setOpportunitiesExpanded ] = useState(false);
    const theme = useTheme();

    const [ tradelineDetails, setTradelineDetails ] = useState({
        title: '',
        transportationMethod: null,
        timespan: {
            start: dayjs().startOf('month').valueOf(),
            end: dayjs().endOf('month').valueOf(),
        },
        routes: [],
        goals: [],
        ...( accountId ? { accountId } : {} )
    });

    const [ errors, setErrors ] = useState([]);
    const [ saving, setSaving ] = useState(false);
    const [ savingSuccess, setSavingSuccess ] = useState(null);
    const [ pipelineStep, setPipelineStep ] = useState(0);
    const [ showDeleteConfirm, setShowDeleteConfirm ] = useState(false);
    const [ deleting, setDeleting ] = useState(false);

    const [ editDetails, setEditDetails ] = useState(tradelineId === 'new');

    useEffect(() => {
        if (tradelineId === 'new') return;
        if (!tradelines) return;
        const tradeline = tradelines.find(t => t.id === tradelineId);
        if (!tradeline) return;
        setTradelineDetails({
            ...tradeline,
            routes: tradeline.routes || [],
            goals: tradeline.goals || [],
        });
        const piplineTag = (tradeline.tags || []).find(t => t.label.startsWith('Pipeline: ')) || { label: PIPELINE[0] };
        setPipelineStep(PIPELINE.indexOf(piplineTag?.label?.replace('Pipeline: ', '')));
    }, [ tradelineId, tradelines ]);

    const { opportunities: filteredOpportunities, results } = useMemo(() => {
        if (!opportunities || !tradelineDetails || editDetails || !exchangeRates) return {};
        return Utils.getTradelinesStatus({ opportunities, exchangeRates, tradeline: tradelineDetails, timespanJump, organization: org })
    }, [ opportunities, tradelineDetails, exchangeRates, timespanJump ])

    const possibleUpdateTags = useMemo(() => {
        if (!tradelines || !Array.isArray(tradelines)) return [];
        return _(tradelines)
            .map(a => a.updates?.map(u => u.tags))
            .flatten()
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
    }, [tradelines]);

    const possibleTags = useMemo(() => {
        if (!tradelines || !Array.isArray(tradelines)) return [];
        const tags = _(tradelines)
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
        return _.xorBy(tags, tradelineDetails?.tags, a => a.label);
    }, [tradelines, tradelineDetails]);

    const account = tradelineDetails.accountId ? accounts?.find(a => a.id === tradelineDetails.accountId) : null;

    function onEditDetails() {
        setAccountInputValue(account?.name || '');
        setCreateNewAccount(false);
        setEditDetails(true);
    }

    async function onSaveDetails({ tradeline = tradelineDetails } = {}) {
        const _errors = [];

        /*
        if (_.isEmpty(tradeline.title)) {
            _errors.push('title');
        }
        */

        if (_.isEmpty(tradeline.accountId) && accountInputValue && !createNewAccount) {
            _errors.push('account');
        }

        if (!tradeline.timespan?.start) {
            _errors.push('timespan');
        }

        if (!tradeline.timespan?.end) {
            _errors.push('timespan');
        }

        if (tradeline.timespan?.start > tradeline.timespan?.end) {
            _errors.push('timespan');
        }

        if (_.some(tradeline.goals, g => {
            if (_.isEmpty(g.type) || _.isEmpty(g.value)) return true;
            const value = g.value.toString().trim();
            return !/^-?\d*\.?\d+$/.test(value) || _.isNaN(parseFloat(value));
        })) {
            _errors.push('goals');
        }

        if (tradeline.visibility?.limited && _.isEmpty(tradeline.visibility?.teams) && _.isEmpty(tradeline.visibility?.users)) {
            _errors.push('visibility');
        }

        tradeline.title = (account?.name || accountInputValue) + ' | ' + _.capitalize(tradeline.transportationMethod);
        if (!_.isEmpty(tradeline.routes)) {
            tradeline.title += ' | ' + (tradeline.routes || []).map(r => {
                const source = r.source?.type !== 'port' ? r.source?.text || 'Everywhere' : r.source?.id || r.source?.text || 'Everywhere';
                const destination = r.destination?.type !== 'port' ? r.destination?.text || 'Everywhere' : r.destination?.id || r.destination?.text || 'Everywhere';
                return source + ' -> ' + destination
            }).join(', ');
        }

        setErrors(_errors);
        if (_errors.length > 0) return;
        setSaving(true);
        tradeline.updates = tradeline.updates
            ?.map(u => !u.pendingDelete ? u : {..._.omit(u, 'pendingDelete'), deleted: true })
            .map(u => !u.editHtml ? u : {..._.omit(u, 'editHtml', 'editTags'), html: u.editHtml, tags: u.editTags })
            .map(u => _.omit(u, 'wasEdited'));
        try {
            if (createNewAccount && accountInputValue && !tradeline.accountId) {
                const { data: newAccount } = await axios.post(`/api/accounts?accesstoken=${accesstoken}&organizationId=${org.id}`, { account: { name: accountInputValue } });
                tradeline.accountId = newAccount.id;
                setAccounts([...(accounts || []), newAccount]);
            }
            if (tradelineId === 'new') {
                const { data: updatedTradeline } = await axios.post(`/api/tradelines?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline: _.omit(tradeline, 'log') });
                const newId = updatedTradeline.id;
                onUpdateTradeline({ tradeline: updatedTradeline, delete: false });
                setSaving(false);
                setSavingSuccess('yes');
                navigate(window.location.pathname.replace('new', newId), { replace: true });
            } else {
                const { data: updatedTradeline } = await axios.put(`/api/tradelines/${tradelineId}?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline: _.omit(tradeline, 'log') });
                onUpdateTradeline({ tradeline: updatedTradeline, delete: false });
                setSaving(false);
                setSavingSuccess('yes');
            }
            setEditDetails(false);
        } catch (e) {
            setSaving(false);
            setSavingSuccess('no');
            setErrors([e.message]);
            console.log('Error saving:', e);
        }

        await new Promise(res => setTimeout(res, 3000));
        setSavingSuccess(null);
    }

    function onOpenOpportunity({ opportunity, newWindow }) {
        if (newWindow) {
            if (!location.pathname || location.pathname.length < 2) {
                window.open(`https://app.monada.ai/organization/${org.alias}/opportunities/${opportunity.id}/?accesstoken=${encodeURIComponent(accesstoken.monada)}`, '_blank');
            } else {
                window.open(`${location.pathname.replace(/\/main/, '')}/${opportunity.id}/?${location.search}`.replace(/\/\//g, '/'), '_blank');
            }
        } else {
            navigate(`/organization/${org.alias}/opportunities/${opportunity.id}/`);
        }
    }

    async function onPipelineStep(index) {
        setPipelineStep(index);
        const tags = (tradelineDetails.tags || []).filter(t => !t.label.startsWith('Pipeline: '));
        tags.push({ label: `Pipeline: ${PIPELINE[index]}` });
        setTradelineDetails({ ...tradelineDetails, tags });

        if (tradelineId !== 'new' && !editDetails) {
            const { data: updatedTradeline } = await axios.put(`/api/tradelines/${tradelineId}?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline: { ..._.omit(tradelineDetails, 'log'), tags } });
            onUpdateTradeline({ tradeline: updatedTradeline, delete: false });
        }
    }

    async function onDeleteUpdate(update) {
        const newTradeline = {
            ...tradelineDetails,
            updates: tradelineDetails.updates.map(u => u.created !== update.created ? u : {...u, pendingDelete: true})
        };
        setTradelineDetails(newTradeline);
        const saveTradeline = {
            ..._.omit(newTradeline, 'log'),
            updates: newTradeline.updates
                ?.map(u => !u.pendingDelete ? u : {..._.omit(u, 'pendingDelete'), deleted: true })
                .map(u => !u.editHtml ? u : {..._.omit(u, 'editHtml', 'editTags'), html: u.editHtml, tags: u.editTags })
                .map(u => _.omit(u, 'wasEdited')),
        };
        try {
            const { data: updatedTradeline } = await axios.put(`/api/tradelines/${tradelineId}?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline: saveTradeline });
            onUpdateTradeline({ tradeline: updatedTradeline, delete: false });
        } catch (e) {
            console.log('Error deleting comment:', e);
        }
    }

    async function onEditUpdateDone({ html, tags, files, prevCreated }) {
        const urls = await Promise.all(files.map(async file => {
            if (file.url) return file;
            const formData = new FormData();
            formData.append('file', file, file.name);
            const { data } = await axios({
                url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${org.id}`,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return { url: data.location, name: file.name };
        }));

        const newTradeline = {
            ...tradelineDetails,
            updates: tradelineDetails.updates.map(u => u.created === prevCreated ? { ...u, editHtml: html, editTags: tags, attachments: urls } : u)
        };
        setTradelineDetails(newTradeline);
        onSaveDetails({ tradeline: newTradeline });
    }

    async function onNewUpdate({ html, files, tags }) {
        const urls = await Promise.all(files.map(async file => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            const { data } = await axios({
                url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${org.id}`,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return { url: data.location, name: file.name };
        }));

        const newTradelineData = {
            ...tradelineDetails, updates: [...(tradelineDetails.updates || []), {
                created: new Date().getTime(),
                createdBy: me.email,
                html,
                tags,
                attachments: urls,
            }]
        };
        setTradelineDetails(newTradelineData);
        onSaveDetails({ tradeline: newTradelineData });
    }

    async function onDeleteTradeline() {
        if (tradelineId === 'new') return;
        setDeleting(true);
        try {
            await axios.delete(`/api/tradelines/${tradelineId}?accesstoken=${accesstoken}&organizationId=${org.id}`);
            onUpdateTradeline({ tradeline: tradelineDetails, delete: true });
            setShowDeleteConfirm(false);
            navigate(-1);
        } catch (e) {
            setDeleting(false);
            console.error('Error deleting tradeline:', e);
        }
    }

    if (!accounts || !tradelines || !exchangeRates || !opportunities || !seaPorts) {
        return <Box sx={{ paddingTop: '36px', background: '#fafbfc', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
        </Box>
    }

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                title={accountId && account ? `${account.name} - ${tradelineDetails.title || 'New Sales Target'}` : tradelineDetails.title || 'New Sales Target'}
                icon={faBullseyeArrow}
                onBack={() => navigate(-1)}
                buttons={_.compact([
                    editDetails && tradelineId !== 'new' ? {
                        title: 'Cancel',
                        icon: faXmark,
                        color: 'error',
                        action: () => {
                            setEditDetails(false);
                            const tradeline = tradelines.find(t => t.id === tradelineId);
                            if (tradeline) setTradelineDetails(tradeline);
                        }
                    } : null,
                    !editDetails && tradelineId !== 'new' ? {
                        title: 'Delete',
                        icon: faTrash,
                        color: 'error',
                        action: () => setShowDeleteConfirm(true),
                    } : null,
                    {
                        title: 'Save',
                        icon: savingSuccess === 'yes' ? faCheck : savingSuccess === 'no' ? faXmark : faFloppyDisk,
                        color: savingSuccess === 'yes' ? 'success' : savingSuccess === 'no' ? 'error' : 'monada-button',
                        action: onSaveDetails,
                        thinking: saving,
                    }
                ])}
                subtitle={
                    tradelineId === 'new' ? null : 
                        (
                        <Autocomplete
                            className={'nonotch'}
                            clearIcon={false}
                            options={possibleTags}
                            filterOptions={(options, state) => {
                                const { inputValue } = state;
                                if (inputValue.length < 1) return [];
                                return _.sortBy(options.filter(a => a.label?.toLowerCase().startsWith(inputValue.toLowerCase())), a => a.label);
                            }}
                            freeSolo
                            multiple
                            renderTags={(value, props) =>
                                value.map((option, index) => (
                                    <Chip label={option.label} {...props({ index })} />
                                ))
                            }
                            value={tradelineDetails.tags?.filter(t => !t.label.startsWith('Pipeline: ')) || []}
                            onChange={(e, value) => {
                                const pipelineTag = tradelineDetails.tags?.find(a => a.label.startsWith('Pipeline: ')) || { label: `Pipeline: ${PIPELINE[0]}` };
                                const tags = value.map(a => typeof (a) === 'string' ? ({ label: a }) : a);
                                setTradelineDetails({ ...tradelineDetails, tags: [...tags, pipelineTag] });
                            }}
                            renderInput={(params) => <TextField placeholder='Start typing to add tags...' sx={{ border: 0 }} InputProps={{ sx: { border: 0 }}} inputProps={{ style: { border: 0 }}} {...params} />}
                        />
                    )
                }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '24px' }}>
                <Paper sx={{ padding: '24px', margin: '0px 12px', flex: 1 }}>
                    <Stepper activeStep={pipelineStep} nonLinear alternativeLabel>
                        {PIPELINE.map((label, index) => (
                            <Step key={label} completed={index <= pipelineStep}>
                                <StepButton onClick={() => onPipelineStep(index)}>
                                    <StepLabel
                                        {...(index <= pipelineStep ? { StepIconComponent: () => <FontAwesomeIcon style={{ fontSize: '24px', color: PIPELINE_COLORS[index] }} icon={faCircleCheck} /> } : {})}
                                    >
                                        {label}
                                        {index === 0 && tradelineDetails.created ? <Typography variant='caption' sx={{ fontSize: '11px', color: grey[500] }} component={Box}>{dayjs(tradelineDetails.created).toString()}</Typography> : null}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>
                </Paper>
            </Box>
            <Paper sx={{ padding: '24px', margin: '12px 12px 0px 12px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                            Details
                        </Typography>
                    </Box>
                    {!editDetails && (
                        <Button variant='text' color='primary' sx={{ textTransform: 'none', display: 'flex', alignItems: 'center' }} onClick={onEditDetails}>
                            <FontAwesomeIcon icon={faPencil} />&nbsp;Edit
                        </Button>
                    )}
                </Box>
                <hr style={{ opacity: 0.2 }} />
                {!editDetails && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        {tradelineDetails.accountId && (
                            <Box sx={{ minWidth: '180px' }}>
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Account</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }} component={Link} to={`/organization/${org.alias}/accounts/${tradelineDetails.accountId}/details`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                                    {account?.name || 'All accounts'}
                                </Typography>
                            </Box>
                        )}
                        {tradelineDetails.transportationMethod && (
                            <Box sx={{ minWidth: '100px' }}>
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Mode</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{_.capitalize(tradelineDetails.transportationMethod)}</Typography>
                            </Box>
                        )}
                        <Box sx={{ minWidth: '180px' }}>
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Period</Typography>
                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                {tradelineDetails.timespanType === 'recurring'
                                    ? {day: 'Daily', week: 'Weekly', month: 'Monthly', quarter: 'Quarterly', year: 'Yearly'}[tradelineDetails.recurringPeriod]
                                    : tradelineDetails.timespan
                                        ? `${dayjs(tradelineDetails.timespan.start).format('MMM D, YYYY')} – ${dayjs(tradelineDetails.timespan.end).format('MMM D, YYYY')}`
                                        : 'Not set'}
                            </Typography>
                        </Box>
                        {tradelineDetails.opportunitiesOwner && (
                            <Box sx={{ minWidth: '150px' }}>
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Owner</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{tradelineDetails.opportunitiesOwner.split('@')[0]}</Typography>
                            </Box>
                        )}
                        {tradelineDetails.transportationMethod && (
                            <Box sx={{ minWidth: '200px', flex: 1 }}>
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Routes</Typography>
                                {(!tradelineDetails.routes || tradelineDetails.routes.length === 0) ? (
                                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>Everywhere</Typography>
                                ) : (tradelineDetails.routes || []).map(r => (
                                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }} key={JSON.stringify(r)}>
                                        {r.source?.text || 'Everywhere'} → {r.destination?.text || 'Everywhere'}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                        {!_.isEmpty(tradelineDetails.goals) && (
                            <Box sx={{ minWidth: '200px', width: '100%' }}>
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Goals</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {(tradelineDetails.goals || []).map(g => (
                                        <Chip key={g.id} label={Utils.goalToText({ goal: g, transportationMethod: tradelineDetails.transportationMethod })} size='small' sx={{ fontSize: '12px' }} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {false && ( /* keeping this to add visibility as a feature in the future */
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant='body1' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>
                                    (Privacy) This tradeline is visible to:
                                </Typography>
                                <Box sx={{ marginLeft: '36px' }}>
                                    <Typography variant='body1' component={Box} sx={{ flex: 1 }}>
                                        {tradelineDetails.owner} (owner)
                                        {tradelineDetails.visibility?.limited && tradelineDetails.visibility?.teams?.length > 0 && tradelineDetails.visibility?.teams?.map(t => <Box key={t}>Team: {t.split(':')[0]} ({t.split(':')[1] || 'missing'})</Box>)}
                                        {tradelineDetails.visibility?.limited && tradelineDetails.visibility?.users?.length > 0 && tradelineDetails.visibility?.users?.map(u => <Box key={u}>User: {u}</Box>)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
                {editDetails && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {false && ( /* keeping this to add visibility as a feature in the future */
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant='caption' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>Title:</Typography>
                                {errors.includes('title') && (
                                    <Alert severity='error'>
                                        Please enter a title for your tradeline.
                                    </Alert>
                                )}
                                <TextField
                                    value={tradelineDetails.title}
                                    onChange={(e) => {
                                        setTradelineDetails({ ...tradelineDetails, title: e.target.value });
                                        setErrors(_.without(errors, 'title'));
                                    }}
                                />
                            </Box>
                        )}
                        {!accountId && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant='caption' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>
                                    Track opportunities from account:
                                </Typography>
                                <AutocompleteWithReactWindow
                                    selectOnFocus
                                    clearOnBlur={tradelineDetails.accountId}
                                    freeSolo={true}
                                    inputValue={accountInputValue}
                                    onInputChange={(e, value) => {
                                        setTradelineDetails({ ...tradelineDetails, accountId: null });
                                        setAccountInputValue(value);
                                    }}
                                    sx={{ width: '100%' }}
                                    disabled={!!accountId}
                                    options={accounts?.map(a => a.name)}
                                    value={accounts?.find(a => a.id === tradelineDetails.accountId)?.name}
                                    onChange={(e, value) => {
                                        setTradelineDetails({ ...tradelineDetails, accountId: accounts?.find(a => a.name === value)?.id });
                                        setErrors(_.without(errors, 'account'));
                                    }}
                                />
                                {accountInputValue && !accounts?.find(a => a.name === accountInputValue) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 3 }}>
                                        <Typography variant='caption' component={Box} color='error'>
                                            Account named "<b>{accountInputValue}</b>" not found.
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(e) => {
                                                        setCreateNewAccount(e.target.checked);
                                                    }}
                                                    icon={<FontAwesomeIcon icon={faSquare} />}
                                                    checkedIcon={<FontAwesomeIcon icon={faSquareCheck} />}
                                                />
                                            }
                                            label={<Typography variant='caption' component={Box} sx={{ fontSize: '13px !important' }}>Create new account</Typography>}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {errors.includes('account') && (
                            <Alert severity='error'>
                                Please select an account, or create a new one from here.
                            </Alert>
                        )}
                        { false && ( /* Keeping this if we want to filter by owner in the future */
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant='caption' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>
                                    Only opportunities owned by:
                                </Typography>
                                <OpportunitiesFilterOwner
                                    org={org}
                                    filter={{ members: [tradelineDetails.opportunitiesOwner] || [] }}
                                    setFilter={(newValue) => {
                                        setTradelineDetails({ ...tradelineDetails, opportunitiesOwner: newValue.members[0] });
                                    }}
                                    me={me}
                                    noSelectionText='Everyone'
                                    singlePick={true}
                                    noMarginRight={true}
                                />
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant='caption' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>
                                Limit to the following routes:
                            </Typography>
                            {errors.includes('routes') && !tradelineDetails.transportationMethod && (
                                <Alert severity='error'>
                                    Please select a transportation method.
                                </Alert>
                            )}
                            {errors.includes('routes') && tradelineDetails.transportationMethod && tradelineDetails.routes.length === 0 && (
                                <Alert severity='error'>
                                    Please enter at least one route.
                                </Alert>
                            )}
                            {errors.includes('routes') && tradelineDetails.transportationMethod && tradelineDetails.routes.length > 0 && (
                                <Alert severity='error'>
                                    One of the routes entered is invalid, please delete it.
                                </Alert>
                            )}
                            <Box sx={{ marginLeft: '36px' }}>
                                <LegEditor
                                    transportationMethod={tradelineDetails.transportationMethod}
                                    setTransportationMethod={method => {
                                        setTradelineDetails({ ...tradelineDetails, transportationMethod: method });
                                        setErrors(_.without(errors, 'routes'));
                                    }}
                                    routes={_.isEmpty(tradelineDetails.routes) ? [{ source: null, destination: null }] : tradelineDetails.routes}
                                    setRoutes={routes => {
                                        setTradelineDetails({ ...tradelineDetails, routes });
                                        setErrors(_.without(errors, 'routes'));
                                    }}
                                    autoPopulateSource={true}
                                    seaPorts={seaPorts}
                                    width={'100%'}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                <Typography variant='caption' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>
                                    Time period (Timezone: {dayjs.tz.guess()}):
                                </Typography>
                                <FormControl>
                                    <RadioGroup
                                        row
                                        value={tradelineDetails.timespanType || 'constant'}
                                        onChange={(e) => setTradelineDetails({...tradelineDetails, timespanType: e.target.value})}
                                    >
                                        <FormControlLabel value='constant' control={<Radio size='small' />} label='Constant' />
                                        <FormControlLabel value='recurring' control={<Radio size='small' />} label='Recurring' />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                            {errors.includes('timespan') && (
                                <Alert severity='error'>
                                    Please enter a valid timespan.
                                </Alert>
                            )}
                            {(tradelineDetails.timespanType || 'constant') === 'constant' && (
                                <Box sx={{ padding: '0px 36px', display: 'flex', flexDirection: 'row', gap: 2 }}>
                                    <TextField
                                        label='Start'
                                        type='date'
                                        value={dayjs(tradelineDetails.timespan?.start).format('YYYY-MM-DD')}
                                        onChange={(e) => {
                                            setTradelineDetails({
                                                ...tradelineDetails, timespan: {
                                                    ...tradelineDetails.timespan,
                                                    start: dayjs(e.target.value).valueOf(),
                                                    end: tradelineDetails.timespan?.end ? dayjs(tradelineDetails.timespan.end).valueOf() : dayjs().endOf('day').valueOf()
                                                }
                                            });
                                            setErrors(_.without(errors, 'timespan'));
                                        }}
                                    />
                                    <TextField
                                        label='End'
                                        type='date'
                                        value={dayjs(tradelineDetails.timespan?.end).format('YYYY-MM-DD')}
                                        onChange={(e) => {
                                            setTradelineDetails({
                                                ...tradelineDetails, timespan: {
                                                    ...tradelineDetails.timespan,
                                                    end: dayjs(e.target.value).valueOf(),
                                                    start: tradelineDetails.timespan?.start ? dayjs(tradelineDetails.timespan.start).valueOf() : dayjs().startOf('day').valueOf()
                                                }
                                            });
                                            setErrors(_.without(errors, 'timespan'));
                                        }}
                                    />
                                </Box>
                            )}
                            {tradelineDetails.timespanType === 'recurring' && (
                                <Box sx={{ padding: '0px 36px' }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Reoccuring Period</InputLabel>
                                        <Select
                                            label='Recurring Period'
                                            value={tradelineDetails.recurringPeriod}
                                            onChange={(e) => setTradelineDetails({ ...tradelineDetails, recurringPeriod: e.target.value })}
                                        >
                                            <MenuItem value='day'>Daily</MenuItem>
                                            <MenuItem value='week'>Weekly</MenuItem>
                                            <MenuItem value='month'>Monthly</MenuItem>
                                            <MenuItem value='quarter'>Quarterly</MenuItem>
                                            <MenuItem value='year'>Yearly</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant='caption' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>
                                I want to achieve the following goals:
                            </Typography>
                            {errors.includes('goals') && tradelineDetails.goals.length === 0 && (
                                <Alert severity='error'>
                                    Please enter at least one goal.
                                </Alert>
                            )}
                            {errors.includes('goals') && tradelineDetails.goals.length > 0 && (
                                <Alert severity='error'>
                                    Goals entered are invalid, please check the errors.
                                </Alert>
                            )}
                            <Box sx={{ marginLeft: '36px' }}>
                                <GoalsEditor
                                    goals={tradelineDetails.goals}
                                    showErrors={errors.includes('goals')}
                                    setGoals={goals => {
                                        setTradelineDetails({ ...tradelineDetails, goals });
                                        setErrors(_.without(errors, 'goals'));
                                    }}
                                    transportationMethod={tradelineDetails.transportationMethod}
                                    organization={org}
                                />
                            </Box>
                        </Box>
                        {false && ( /* keeping this to add visibility as a feature in the future */
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant='caption' component={Box} sx={{ fontWeight: 800, flex: 0, whiteSpace: 'nowrap' }}>
                                    (Privacy) This tradeline is visible to:
                                </Typography>
                                <Box sx={{ marginLeft: '36px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {errors.includes('visibility') && (
                                        <Alert severity='error'>
                                            Please select a team or user for visibility.
                                        </Alert>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <FormControl>
                                            <RadioGroup
                                                value={tradelineDetails.visibility?.limited ? 'limited' : 'only-me'}
                                                onChange={(e) => {
                                                    const isLimited = e.target.value === 'limited';
                                                    setTradelineDetails({
                                                        ...tradelineDetails,
                                                        visibility: {
                                                            ...tradelineDetails.visibility,
                                                            limited: isLimited,
                                                            ...(isLimited ? {} : {
                                                                teams: [],
                                                                users: []
                                                            })
                                                        }
                                                    });
                                                }}
                                                row
                                            >
                                                <FormControlLabel
                                                    value='only-me'
                                                    control={<Radio />}
                                                    label='Only me'
                                                />
                                                <FormControlLabel
                                                    value='limited'
                                                    control={<Radio />}
                                                    label='Me and...'
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>

                                    {tradelineDetails.visibility?.limited && (
                                        <>
                                            <Typography variant='caption' component={Box} sx={{ flex: 1, fontWeight: 800 }}>Teams:</Typography>
                                            <OpportunitiesFilterOwner
                                                org={org}
                                                filter={{ members: tradelineDetails.visibility?.teams || [] }}
                                                setFilter={(newValue) => {
                                                    setTradelineDetails({ ...tradelineDetails, visibility: { ...tradelineDetails.visibility, teams: newValue.members } });
                                                }}
                                                me={me}
                                                teamLevel={true}
                                                noSelectionText='No teams selected'
                                            />
                                            <Typography variant='caption' component={Box} sx={{ flex: 1, fontWeight: 800 }}>Specific users:</Typography>
                                            <OpportunitiesFilterOwner
                                                org={org}
                                                filter={{ members: tradelineDetails.visibility?.users || [] }}
                                                setFilter={(newValue) => {
                                                    setTradelineDetails({ ...tradelineDetails, visibility: { ...tradelineDetails.visibility, users: newValue.members } });
                                                }}
                                                me={me}
                                                noSelectionText='No users selected'
                                            />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
            {!editDetails && !_.isEmpty(filteredOpportunities) && (
                <Paper sx={{ padding: '24px', margin: '12px 12px', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                            <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                                Progress
                            </Typography>
                            <Box>
                                <IconButton
                                    onClick={() => setTimespanJump(timespanJump - 1)}
                                    sx={{ visibility: tradelineDetails.timespanType === 'recurring' ? 'visible' : 'hidden' }}
                                >
                                    <FontAwesomeIcon icon={faCaretLeft} />
                                </IconButton>
                                <Typography variant='caption'>
                                    {dayjs(Utils.getTradelineTimespan({ tradeline: tradelineDetails, timespanJump })?.start).format('L')} - {dayjs(Utils.getTradelineTimespan({ tradeline: tradelineDetails, timespanJump })?.end).format('L')}
                                </Typography>
                                <IconButton
                                    onClick={() => setTimespanJump(timespanJump + 1)}
                                    sx={{ visibility: tradelineDetails.timespanType === 'recurring' ? 'visible' : 'hidden' }}
                                >
                                    <FontAwesomeIcon icon={faCaretRight} />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                    <hr style={{ opacity: 0.2 }} />
                    <GoalsStatus
                        goals={tradelineDetails.goals}
                        results={results}
                        transportationMethod={tradelineDetails.transportationMethod}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '24px 0px 12px 0px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                            <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                                Opportunities ({filteredOpportunities.length})
                            </Typography>
                        </Box>
                        <Box>
                            <Button variant='outlined' color='primary' onClick={() => setOpportunitiesExpanded(!opportunitiesExpanded)}>
                                {opportunitiesExpanded ? 'Hide' : 'Show'}
                            </Button>
                        </Box>
                    </Box>
                    {opportunitiesExpanded && (
                        <>
                            <hr style={{ opacity: 0.2 }} />
                            {_(filteredOpportunities).sortBy(o => -1 * o.created).map(o => (
                                <OpportunityRow
                                    opportunity={o}
                                    onOpen={onOpenOpportunity}
                                    accesstoken={accesstoken}
                                    onUpdateOpportunity={onUpdateOpportunity}
                                    accounts={accounts || []}
                                    organization={org}
                                />
                            )).value()}
                        </>
                    )}
                </Paper>
            )}

            {_.isEmpty(filteredOpportunities) && (
                <Paper sx={{ padding: '24px', margin: '12px 12px', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                            <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                                Progress
                            </Typography>
                            <Box>
                                <IconButton
                                    onClick={() => setTimespanJump(timespanJump - 1)}
                                    sx={{ visibility: tradelineDetails.timespanType === 'recurring' ? 'visible' : 'hidden' }}
                                >
                                    <FontAwesomeIcon icon={faCaretLeft} />
                                </IconButton>
                                <Typography variant='caption'>
                                    {dayjs(Utils.getTradelineTimespan({ tradeline: tradelineDetails, timespanJump })?.start).format('L')} - {dayjs(Utils.getTradelineTimespan({ tradeline: tradelineDetails, timespanJump })?.end).format('L')}
                                </Typography>
                                <IconButton
                                    onClick={() => setTimespanJump(timespanJump + 1)}
                                    sx={{ visibility: tradelineDetails.timespanType === 'recurring' ? 'visible' : 'hidden' }}
                                >
                                    <FontAwesomeIcon icon={faCaretRight} />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                    <hr style={{ opacity: 0.2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: 1, mt: 2 }}>
                        <FontAwesomeIcon icon={faRadar} size='2x' color={theme.palette.text.secondary} />
                        <Typography variant='body2' component={Box} sx={{ flex: 1, textAlign: 'center', color: theme.palette.text.secondary }}>
                            No opportunities found.
                        </Typography>
                    </Box>
                </Paper>
            )}

            {tradelineId !== 'new' && (
                <Paper sx={{ padding: '24px', margin: '12px 12px', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                                Timeline
                            </Typography>
                        </Box>
                    </Box>
                    <hr style={{ opacity: 0.2 }} />

                    <MonadaTimeline
                        updates={tradelineDetails.updates || []}
                        log={tradelineDetails.log || []}
                        reminders={tradelineDetails.reminders}
                        onDeleteUpdate={onDeleteUpdate}
                        onEditUpdateDone={onEditUpdateDone}
                        onNewUpdate={onNewUpdate}
                        onOpenOpportunity={onOpenOpportunity}
                        onUpdateOpportunity={onUpdateOpportunity}
                        me={me}
                        possibleUpdateTags={possibleUpdateTags}
                        featureToggle={featureToggle}
                        accounts={accounts}
                        organization={org}
                        draftKey={`comment-draft-tradeline-${tradelineId}`}
                    />
                </Paper>
            )}
            <ConfirmDialog
                open={showDeleteConfirm}
                onClose={() => !deleting && setShowDeleteConfirm(false)}
                onConfirm={onDeleteTradeline}
                color='error'
            >
                <Typography variant='body1' sx={{ padding: '12px' }}>
                    Are you sure you want to delete this tradeline? This action cannot be undone.
                </Typography>
                {deleting && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
            </ConfirmDialog>
        </Box>
    );
}

function GoalsEditor(props) {
    const { goals, setGoals, transportationMethod, showErrors, organization } = props;

    const safeGoals = goals || [];

    function addGoal() {
        setGoals([...safeGoals, { id: uuidv4(), type: 'units', value: 0, kgOrLbs: 'kg', currency: organization.defaults?.currency }]);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}> 
            {safeGoals.map(g => (
                <GoalEditor
                    key={g.id}
                    goal={g}
                    setGoal={updateGoal => setGoals(safeGoals.map(g2 => g2.id === g.id ? updateGoal : g2))}
                    transportationMethod={transportationMethod}
                    onDelete={() => setGoals(goals.filter(g2 => g2.id !== g.id))}
                    showErrors={showErrors}
                    organization={organization}
                />
            ))}
            <Button variant='outlined' sx={{ padding: '12px 0px' }} fullWidth color='primary' onClick={addGoal} disabled={!transportationMethod}>
                + Add Goal
            </Button>
        </Box>
    )
}

function GoalEditor(props) {
    const { goal, setGoal, onDelete, transportationMethod, showErrors, organization } = props;

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: '400px' }}>   
                <InputLabel>Type</InputLabel>
                <Select label='Type' value={goal.type} onChange={(e) => setGoal({ ...goal, type: e.target.value })}>
                    {(Utils.GOAL_TYPES[transportationMethod] || Utils.GOAL_TYPES.sea).map(g => (
                        <MenuItem value={g.type}>{g.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            {goal.type === 'units' && (
                <TextField
                    label='Value'
                    fullWidth
                    value={goal.value}
                    onChange={(e) => setGoal({ ...goal, value: e.target.value })}
                    error={showErrors && !/^-?\d*\.?\d+$/.test(goal.value)}
                />
            ) }
            {goal.type === 'shipments' && (
                <TextField 
                    label='Value' 
                    fullWidth 
                    value={goal.value} 
                    onChange={(e) => setGoal({ ...goal, value: e.target.value })} 
                    error={showErrors && !/^-?\d*\.?\d+$/.test(goal.value)} 
                />
            ) }
            {(goal.type === 'chargeable-weight' || goal.type === 'gross-weight') && (
                <FormControl sx={{ flex: 1 }}>
                    <TextField
                        variant='outlined'
                        label='Weight'
                        id='weight'
                        value={goal.value}
                        onChange={(event) => { setGoal({ ...goal, value: event.target.value }) }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <span
                                        style={{ cursor: 'pointer', color: blue[500] }}
                                        onClick={() => setGoal({ ...goal, kgOrLbs: goal.kgOrLbs === 'kg' ? 'lbs' : 'kg' })}
                                    >
                                        {goal.kgOrLbs}
                                    </span>
                                </InputAdornment>
                            )
                        }}
                        error={showErrors && !/^-?\d*\.?\d+$/.test(goal.value)}
                    />
                </FormControl>
            )}
            {(goal.type === 'revenue' || goal.type === 'profit') && (
                <CurrencyTextField
                    value={goal.value}
                    setValue={value => setGoal({ ...goal, value })}
                    currency={goal.currency}
                    setCurrency={currency => setGoal({ ...goal, currency })}
                    width={'100%'}
                    error={showErrors && !/^-?\d*\.?\d+$/.test(goal.value)}
                    organization={organization}
                />
            )}
            <div style={{ flex: 0 }}>
                <IconButton
                    color='error'
                    onClick={onDelete}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </IconButton>
            </div>

        </Box>
    );
}

function GoalsStatus(props) {
    const { goals, results, transportationMethod } = props;

    const safeGoals = goals || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {safeGoals.map(g => (
                <GoalStatus
                    key={g.id}
                    goal={g}
                    results={results?.[g.id]}
                    transportationMethod={transportationMethod}
                />
            ))}
        </Box>
    )
}

function GoalStatus(props) {
    const { goal, results, transportationMethod } = props;

    const progress = Math.round((results?.progress || 0) * 100);
    const actual = results?.value ?? 0;
    const target = results?.goal ?? goal.value ?? 0;
    const isComplete = progress >= 100;
    const barColor = isComplete ? green[500] : progress >= 50 ? amber[500] : red[400];

    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const showCurrency = (goal.type === 'revenue' || goal.type === 'profit') && goal.currency;
    const formatValue = (v) => showCurrency ? `${goal.currency} ${fraction.format(v)}` : fraction.format(v);

    const label = goal.type === 'units' ? (transportationMethod === 'air' ? 'Freight Units' : 'TEUs')
        : goal.type === 'shipments' ? 'Shipments'
        : goal.type === 'chargeable-weight' ? 'Chargeable Weight'
        : goal.type === 'gross-weight' ? 'Gross Weight'
        : goal.type === 'revenue' ? 'Revenue'
        : goal.type === 'profit' ? 'Profit'
        : goal.type;

    return (
        <Box sx={{ flex: 1, minWidth: '280px', maxWidth: '450px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', backgroundColor: '#fff' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>{label}</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isComplete ? green[700] : '#334155' }}>
                    {progress}%
                </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 10, borderRadius: 5, backgroundColor: grey[200], mb: 1.5 }}>
                <Box sx={{
                    width: `${Math.min(100, progress)}%`,
                    height: '100%',
                    borderRadius: 5,
                    backgroundColor: barColor,
                    transition: 'width 0.3s ease',
                }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                    Actual: <b style={{ color: '#334155' }}>{formatValue(actual)}</b>
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                    Goal: <b style={{ color: '#334155' }}>{formatValue(target)}</b>
                </Typography>
            </Box>
        </Box>
    )
}

export default TradelinePage;